import type { UnitSlug } from "@/data/units";

/**
 * Fonte única dos planos e do Day Use.
 * Todos os valores são MENSAIS (nunca o total do período).
 * Para atualizar preços, edite apenas este arquivo.
 */

export type PlanId = "mensal" | "trimestral" | "semestral" | "anual" | "executivo" | "executivo-sabado";

export type Plan = {
  id: PlanId;
  name: string;
  /** valor mensal em reais */
  monthly: number;
  /** texto do período, ex.: "por 3 meses" */
  period: string;
  /** plano com horários restritos (mostra os horários do Executivo) */
  executive?: boolean;
};

export type UnitPlans = {
  plans: Plan[];
  /** ids exibidos no card resumido da home */
  preview: PlanId[];
  note?: string;
};

export const DAY_USE = {
  name: "Day Use Nook",
  price: 100,
  description: "Conheça na prática o método, o acompanhamento e a experiência Nook.",
  credit: "O valor pago pelo Day Use é abatido da mensalidade ao iniciar um plano.",
} as const;

/** Horários do Executivo — iguais nas três unidades. */
export const EXECUTIVE_HOURS = {
  days: "Segunda a sexta-feira",
  slots: ["12h às 13h30", "13h30 às 15h", "15h às 16h30", "21h"],
} as const;

const PREVIEW: PlanId[] = ["mensal", "trimestral", "semestral"];

const BARRA_AMERICAS: Plan[] = [
  { id: "mensal", name: "Mensal", monthly: 699, period: "Mensal" },
  { id: "trimestral", name: "Trimestral", monthly: 562.35, period: "por 3 meses" },
  { id: "semestral", name: "Semestral", monthly: 539.35, period: "por 6 meses" },
  { id: "executivo", name: "Executivo", monthly: 439, period: "Horários reduzidos", executive: true },
  { id: "executivo-sabado", name: "Executivo + Sábado", monthly: 499, period: "Horários reduzidos", executive: true },
];

export const PLANS: Record<UnitSlug, UnitPlans> = {
  "recreio-le-gusta": {
    preview: PREVIEW,
    plans: [
      { id: "mensal", name: "Mensal", monthly: 549, period: "Mensal" },
      { id: "trimestral", name: "Trimestral", monthly: 479, period: "por 3 meses" },
      { id: "semestral", name: "Semestral", monthly: 459, period: "por 6 meses" },
      { id: "anual", name: "Anual", monthly: 429, period: "por 12 meses" },
      { id: "executivo", name: "Executivo", monthly: 380, period: "Horários reduzidos", executive: true },
      { id: "executivo-sabado", name: "Executivo + Sábado", monthly: 410, period: "Horários reduzidos", executive: true },
    ],
    note: "Em caso de recorrência: acréscimo de R$ 10,00.",
  },
  "barra-da-tijuca": { preview: PREVIEW, plans: BARRA_AMERICAS },
  "recreio-americas": { preview: PREVIEW, plans: BARRA_AMERICAS },
};

const BRL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

export function formatBRL(value: number): string {
  // Intl insere espaço não separável entre "R$" e o número — evita quebra no meio do preço
  return BRL.format(value);
}

export function previewPlans(slug: UnitSlug): Plan[] {
  const p = PLANS[slug];
  return p.preview.map((id) => p.plans.find((x) => x.id === id)!).filter(Boolean);
}
